import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, TextField, Button, List, ListItem, ListItemText, IconButton, Chip, Stack } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import notificationsApi, { NotificationTemplate, CreateNotificationTemplateRequest, UpdateNotificationTemplateRequest } from '../../services/api/notifications-advanced.api';
import { PAGE_SIZE } from '../../constants/pagination';

interface Props {
  open: boolean;
  onClose: () => void;
}

const TemplatesManager: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation('common');
  const [items, setItems] = useState<NotificationTemplate[]>([]);
  const [name, setName] = useState('');
  const [type, setType] = useState('system');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [variables, setVariables] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await notificationsApi.getNotificationTemplates({ limit: PAGE_SIZE.MAX });
      const data = (res as any).data || res;
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const handleCreate = async () => {
    const payload: CreateNotificationTemplateRequest = {
      name,
      type,
      subject,
      message,
      variables: variables ? variables.split(',').map(v => v.trim()).filter(Boolean) : [],
    };
    setLoading(true);
    try {
      await notificationsApi.createNotificationTemplate(payload);
      setName(''); setType('system'); setSubject(''); setMessage(''); setVariables('');
      await load();
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (id: string, changes: UpdateNotificationTemplateRequest) => {
    setLoading(true);
    try {
      await notificationsApi.updateNotificationTemplate(id, changes);
      setEditingId(null);
      await load();
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    setLoading(true);
    try {
      await notificationsApi.deleteNotificationTemplate(id);
      await load();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{t('notificationUI.templatesTitle')}</DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={1} sx={{ my: 1 }}>
          <TextField label={t('notificationUI.name')} size="small" value={name} onChange={e => setName(e.target.value)} fullWidth />
          <TextField label={t('notificationUI.type')} size="small" value={type} onChange={e => setType(e.target.value)} sx={{ width: 160 }} />
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <TextField label={t('notificationUI.subject')} size="small" value={subject} onChange={e => setSubject(e.target.value)} fullWidth />
          <TextField label={t('notificationUI.variablesHint')} size="small" value={variables} onChange={e => setVariables(e.target.value)} fullWidth />
        </Stack>
        <TextField label={t('notificationUI.message')} size="small" value={message} onChange={e => setMessage(e.target.value)} fullWidth multiline minRows={3} sx={{ mb: 1 }} />
        <Button variant="contained" onClick={handleCreate} startIcon={<AddIcon />} disabled={loading || !name || !message}>{t('notificationUI.add')}</Button>

        <List>
          {items.map(t => (
            <ListItem key={t.id} secondaryAction={
              <Box>
                <IconButton edge="end" onClick={() => setEditingId(editingId === t.id ? null : t.id)}>
                  <EditIcon />
                </IconButton>
                <IconButton edge="end" onClick={() => handleDelete(t.id)} disabled={loading}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            }>
              <ListItemText
                primary={<Box display="flex" alignItems="center" gap={1}>
                  <strong>{t.name}</strong>
                  <Chip size="small" label={t.type} />
                  {!t.isActive && <Chip size="small" label="disabled" />}
                </Box>}
                secondary={<>
                  <div>Тема: {t.subject}</div>
                  <div>Переменные: {(t.variables || []).join(', ') || '—'}</div>
                </>}
              />
            </ListItem>
          ))}
        </List>

        {editingId && (() => {
          const item = items.find(i => i.id === editingId)!;
          return (
            <Box sx={{ mt: 2 }}>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <TextField label="Название" size="small" defaultValue={item.name} onBlur={e => item.name = e.target.value} fullWidth />
                <TextField label="Тип" size="small" defaultValue={item.type} onBlur={e => (item as any).type = e.target.value} sx={{ width: 160 }} />
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <TextField label="Тема" size="small" defaultValue={item.subject} onBlur={e => item.subject = e.target.value} fullWidth />
                <TextField label="Переменные (через запятую)" size="small" defaultValue={(item.variables || []).join(', ')} onBlur={e => (item as any).variables = e.target.value.split(',').map((v: string) => v.trim()).filter(Boolean)} fullWidth />
              </Stack>
              <TextField label="Сообщение" size="small" defaultValue={item.message} onBlur={e => item.message = e.target.value} fullWidth multiline minRows={3} sx={{ mb: 1 }} />
              <Button variant="contained" startIcon={<SaveIcon />} onClick={() => handleUpdate(item.id, { name: item.name, type: (item as any).type, subject: item.subject, message: item.message, variables: (item as any).variables })} disabled={loading}>Сохранить изменения</Button>
            </Box>
          );
        })()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('team.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplatesManager;


